import React, { useEffect, useMemo, useState } from "react";
import { apiGet, apiPut } from "../../../services/api";
import ClinicNotesWidget from "./ClinicNotesWidget";
import "./FollowupManager.css";

interface Followup {
  id: number;
  patient_id: string;
  patient_name: string;
  patient_mobile: string;
  age: number;
  gender: string;
  procedure_type: string;
  tooth_no: string | null;
  followup_stage: string;
  scheduled_date: string;
  actual_date?: string | null;
  status: string;
  further_action_needed?: boolean | null;
}

function toDateLabel(d: string | null | undefined): string {
  if (!d) return "—";
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return String(d);
  return t.toLocaleDateString();
}

function isCompleted(item: Followup): boolean {
  return (item.status || "").toUpperCase() === "COMPLETED";
}

function isScheduled(item: Followup): boolean {
  return (item.status || "").toUpperCase() === "SCHEDULED";
}

export default function FollowupManager() {
  const lastDays = 7;

  const [queue, setQueue] = useState<Followup[]>([]);
  const [history, setHistory] = useState<Followup[]>([]);
  const [loadingQueue, setLoadingQueue] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const [decisionModal, setDecisionModal] = useState<{ open: boolean; item: Followup | null }>({
    open: false,
    item: null,
  });

  const refresh = () => {
    setLoadingQueue(true);
    apiGet(`/api/followups?bucket=queue&last_days=${lastDays}&limit=500`)
      .then((data: unknown) => setQueue(Array.isArray(data) ? (data as Followup[]) : []))
      .catch((e) => {
        console.error("Failed to load followup queue", e);
        setQueue([]);
      })
      .finally(() => setLoadingQueue(false));

    setLoadingHistory(true);
    apiGet(`/api/followups?bucket=history&last_days=${lastDays}&limit=500`)
      .then((data: unknown) => setHistory(Array.isArray(data) ? (data as Followup[]) : []))
      .catch((e) => {
        console.error("Failed to load followup history", e);
        setHistory([]);
      })
      .finally(() => setLoadingHistory(false));
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sortedQueue = useMemo(() => {
    const weight = (f: Followup) => {
      if (isScheduled(f)) return 3;
      if (isCompleted(f) && f.further_action_needed == null) return 2; // decision pending
      if (isCompleted(f) && f.further_action_needed === true) return 1; // finished, action needed
      return 0;
    };
    return [...queue].sort((a, b) => {
      const w = weight(b) - weight(a);
      if (w !== 0) return w;
      const da = new Date((a.actual_date || a.scheduled_date) as string).getTime();
      const db = new Date((b.actual_date || b.scheduled_date) as string).getTime();
      return db - da;
    });
  }, [queue]);

  const setDecision = async (item: Followup, furtherActionNeeded: boolean) => {
    try {
      await apiPut(`/api/followups/${item.id}/status`, {
        status: "COMPLETED",
        further_action_needed: furtherActionNeeded,
      });
      setDecisionModal({ open: false, item: null });
      refresh();
    } catch (e) {
      console.error("Failed to update followup decision", e);
      alert("Failed to update followup");
    }
  };

  return (
    <div className="manager-wrapper fu-wrapper">
      <div className="fu-grid">
        <div className="fu-panel">
          <div className="fu-head">
            <div>
              <h3>📞 Follow-ups (Last {lastDays} Days)</h3>
              <div className="fu-sub">Pending + Finished (needs decision / further action)</div>
            </div>
            <button className="fu-icon-btn" type="button" onClick={refresh} title="Refresh">
              ↻
            </button>
          </div>

          <div className="fu-list">
            {loadingQueue ? (
              <div className="fu-empty">Loading…</div>
            ) : sortedQueue.length === 0 ? (
              <div className="fu-empty">No follow-ups in this window.</div>
            ) : (
              sortedQueue.map((item) => {
                const finished = isCompleted(item);
                const pending = isScheduled(item);
                const showDecision = pending || (finished && item.further_action_needed == null);
                const showMoveToHistory = finished && item.further_action_needed === true;
                const badge =
                  pending ? "Pending" : finished ? "Finished" : (item.status || "—").toUpperCase();

                return (
                  <div key={item.id} className="fu-card">
                    <div className="fu-card-top">
                      <div className="fu-main">
                        <div className="fu-name">{item.patient_name}</div>
                        <div className="fu-meta">
                          {item.age}Y / {item.gender} • 📞 {item.patient_mobile}
                        </div>
                        <div className="fu-meta">
                          {item.procedure_type} • {item.followup_stage}
                          {item.tooth_no ? ` • Tooth ${item.tooth_no}` : ""}
                        </div>
                      </div>
                      <div className="fu-right">
                        <div className={`fu-badge ${pending ? "pending" : "finished"}`}>{badge}</div>
                        {finished && item.further_action_needed === true && (
                          <div className="fu-badge action">Action Needed</div>
                        )}
                        {finished && item.further_action_needed == null && (
                          <div className="fu-badge decide">Decide: Yes/No</div>
                        )}
                        <div className="fu-date">
                          {toDateLabel(item.actual_date || item.scheduled_date)}
                        </div>
                      </div>
                    </div>

                    <div className="fu-actions">
                      {showDecision && (
                        <button
                          className="fu-btn success"
                          type="button"
                          onClick={() => setDecisionModal({ open: true, item })}
                        >
                          Followup Done → Further action?
                        </button>
                      )}
                      {showMoveToHistory && (
                        <button
                          className="fu-btn info"
                          type="button"
                          onClick={() => setDecision(item, false)}
                        >
                          No further action (Move to History)
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="fu-right-col">
          <div className="fu-panel">
            <div className="fu-head">
              <div>
                <h3>📜 History</h3>
                <div className="fu-sub">Completed with “No further action” (last {lastDays} days)</div>
              </div>
              <button className="fu-icon-btn" type="button" onClick={refresh} title="Refresh">
                ↻
              </button>
            </div>

            <div className="fu-list">
              {loadingHistory ? (
                <div className="fu-empty">Loading…</div>
              ) : history.length === 0 ? (
                <div className="fu-empty">No history records.</div>
              ) : (
                history.map((item) => (
                  <div key={item.id} className="fu-history-row">
                    <div className="fu-history-top">
                      <div className="fu-name">{item.patient_name}</div>
                      <div className="fu-date">{toDateLabel(item.actual_date || item.scheduled_date)}</div>
                    </div>
                    <div className="fu-meta">
                      {item.procedure_type} • {item.followup_stage}
                      {item.tooth_no ? ` • Tooth ${item.tooth_no}` : ""}
                    </div>
                    <div className="fu-meta">📞 {item.patient_mobile}</div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="fu-panel fu-notes">
            <ClinicNotesWidget />
          </div>
        </div>
      </div>

      {decisionModal.open && decisionModal.item && (
        <div className="fu-modal-overlay" onClick={() => setDecisionModal({ open: false, item: null })}>
          <div className="fu-modal" onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Followup Done?</h3>
            <div className="fu-sub" style={{ marginBottom: 12 }}>
              {decisionModal.item.patient_name} • {decisionModal.item.procedure_type} • {decisionModal.item.followup_stage}
            </div>

            <div style={{ fontWeight: 800, marginBottom: 10 }}>Further action needed?</div>
            <div className="fu-modal-actions">
              <button className="fu-btn success" type="button" onClick={() => setDecision(decisionModal.item as Followup, false)}>
                1) No
              </button>
              <button className="fu-btn warn" type="button" onClick={() => setDecision(decisionModal.item as Followup, true)}>
                2) Yes
              </button>
              <button className="fu-btn" type="button" onClick={() => setDecisionModal({ open: false, item: null })}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

